/// Advanced dinghy techniques — NSS Levels 3 + 4. Extends the basic
/// sail-handling sequences in `code.ts` with the racing-grade skills:
/// sailing without rudder, roll tacks + roll gybes, spinnaker
/// handling, trapeze, and launching + recovery.

// ───────────────────────────────────────────────────────────────────
// 1. Advanced dinghy techniques
// ───────────────────────────────────────────────────────────────────

export type AdvancedTechniqueId =
  | "sailing-without-rudder"
  | "roll-tack"
  | "roll-gybe"
  | "spinnaker-hoist"
  | "spinnaker-drop"
  | "spinnaker-gybe"
  | "trapeze";

export interface AdvancedTechnique {
  id: AdvancedTechniqueId;
  name: string;
  /// NSS course level where this is first taught.
  course: "NSS L3" | "NSS L4";
  /// One-line gist.
  summary: string;
  /// When you'd use this technique.
  whenToUse: string;
  /// Step-by-step procedure.
  steps: readonly string[];
  /// Common mistakes.
  pitfalls: readonly string[];
}

export const ADVANCED_TECHNIQUES: readonly AdvancedTechnique[] = Object.freeze([
  {
    id: "sailing-without-rudder",
    name: "Sailing without a rudder",
    course: "NSS L3",
    summary:
      "Steer the boat using sail trim + crew weight alone. Builds sail-balance intuition + saves the day if the rudder is lost.",
    whenToUse:
      "Practice in benign conditions; emergency if you lose the rudder; demonstrates the Five Essentials at work.",
    steps: [
      "Lift the rudder out of the water (or remove it entirely for practice).",
      "Crew + helm position weight LEEWARD to make the boat turn TO WINDWARD.",
      "Sheet IN the main + ease the jib to turn TO WINDWARD (more main = more weather helm).",
      "Sheet IN the jib + ease the main to turn TO LEEWARD (more jib = lee helm).",
      "Crew + helm weight to WINDWARD makes the boat turn TO LEEWARD.",
      "Combine sail trim + crew weight for fine course control.",
      "Plan ALL course changes — there's no quick rudder correction available.",
    ],
    pitfalls: [
      "Over-trimming the main into a death roll downwind — depower fast by easing both sheets.",
      "Trying to point too high — boat stalls; bear away by easing main + crew weight to leeward.",
      "Tacking without enough boat speed — boat ends up in irons.",
    ],
  },
  {
    id: "roll-tack",
    name: "Roll tack",
    course: "NSS L3",
    summary:
      "Use crew weight to ROLL the boat through the tack — accelerating out the other side faster than a flat tack.",
    whenToUse:
      "Light + moderate wind. Racing technique; gives speed advantage out of each tack.",
    steps: [
      "Approach the tack at full speed close-hauled.",
      "Helm calls 'Ready about'. Crew responds 'Ready'.",
      "Helm calls 'Lee-oh' + steers the boat into the tack.",
      "AS the bow goes through the wind, crew + helm roll the boat hard to WINDWARD (leaning over the OLD windward side).",
      "Boat is now rolling onto the OLD windward side (now leeward of the new tack).",
      "As the boat passes through head-to-wind, both crew + helm flip to the NEW windward side + flatten the boat with a quick weight shift.",
      "The 'pump' from the flatten accelerates the boat onto the new course.",
      "Trim sails immediately for the new tack.",
    ],
    pitfalls: [
      "Rolling too late — boat falls flat before the helm can transfer weight + the pump is lost.",
      "Over-rolling — boat goes too far + capsizes mid-tack.",
      "Trying to roll-tack in too much wind — boat overpowers + capsizes.",
    ],
  },
  {
    id: "roll-gybe",
    name: "Roll gybe",
    course: "NSS L3",
    summary:
      "Use crew weight to ROLL the boat through the gybe — controlled boom swing + speed gain out.",
    whenToUse:
      "Light + moderate wind downwind. Faster than a flat gybe; demonstrates skill.",
    steps: [
      "Approach the gybe at full speed running.",
      "Helm calls 'Stand by to gybe'. Crew responds 'Ready'.",
      "Crew + helm shift weight to the OLD leeward side — boat rolls toward new gybe.",
      "Helm bears away as the boat rolls.",
      "Boom crosses CONTROLLED by the roll (not by the helm forcing it).",
      "As the boom crosses, crew + helm immediately flip to the NEW windward side + flatten the boat.",
      "The flatten provides the same speed-pump as a roll tack.",
      "Settle on the new course; trim sails.",
    ],
    pitfalls: [
      "Roll initiated too late — boom crashes across without the controlled arc.",
      "Crew on wrong side at the moment of the gybe — capsize.",
      "Too much wind — roll-gybe becomes a death-roll. Use a controlled flat gybe instead.",
    ],
  },
  {
    id: "spinnaker-hoist",
    name: "Spinnaker — hoist",
    course: "NSS L3",
    summary:
      "Get the spinnaker up + filling, with the pole set + the sail sheeted in.",
    whenToUse:
      "Off-wind sailing — broad reach to dead run. Adds significant power + speed.",
    steps: [
      "Crew sets up: pole on the mast track, ready to clip onto the guy.",
      "Helm bears away to broad reach so the boat is sailing slowly + relatively flat.",
      "Crew bags the spinnaker so the head + tack + clew are accessible at the bag mouth.",
      "Tack to the pole end, sheet to the cockpit, halyard to the bag's head.",
      "Pull the halyard FAST — spinnaker rises out of the bag + opens as it goes.",
      "As the spinnaker fills, sheet IN to set the trim.",
      "Helm holds course while crew trims spinnaker + adjusts pole height.",
    ],
    pitfalls: [
      "Slow hoist — spinnaker fills part-way + flogs against the rig; halyard up FAST.",
      "Pole not pre-set — spinnaker flies away from the boat without the pole.",
      "Guy + sheet swapped — spinnaker fills inverted + crashes back.",
      "Hoist in too much wind without practising — broach + capsize.",
    ],
  },
  {
    id: "spinnaker-drop",
    name: "Spinnaker — drop",
    course: "NSS L3",
    summary:
      "Get the spinnaker down + into the bag, with the pole stowed + the boat under control.",
    whenToUse:
      "End of off-wind leg; approaching a mark or shore; need to slow + manoeuvre.",
    steps: [
      "Helm bears away slightly to leeward to depower the spinnaker.",
      "Crew unclips the spinnaker tack from the pole + holds the tack.",
      "Halyard released slightly — spinnaker collapses forward + behind the main.",
      "Crew gathers the spinnaker into the bag, sheet end first.",
      "Once fully gathered, halyard fully released + spinnaker stowed.",
      "Pole stowed on mast.",
      "Helm returns to course; main + jib re-trimmed for the new heading.",
    ],
    pitfalls: [
      "Drop in too much wind without practice — spinnaker tears or fills the cockpit with cloth.",
      "Halyard released too fast — spinnaker drops in the water + drags.",
      "Pole not properly stowed — fouls during next tack.",
    ],
  },
  {
    id: "spinnaker-gybe",
    name: "Spinnaker — gybe",
    course: "NSS L4",
    summary:
      "Move the spinnaker pole + sheets from one tack to the other while sailing downwind.",
    whenToUse:
      "Downwind course changes; tacking downwind for tactical advantage.",
    steps: [
      "Helm bears away to dead-run.",
      "Helm calls 'Stand by to gybe'. Crew prepares.",
      "Helm calls 'Gybe-oh' + steers to bring the stern through the wind.",
      "As the main gybes across, crew releases the OLD guy + the OLD sheet — spinnaker now flies free.",
      "Crew detaches the pole from the OLD tack + attaches it to the NEW tack.",
      "Crew sheets the NEW sheet (formerly the guy) + sets the new guy (formerly the sheet) through the pole.",
      "Spinnaker re-fills on the new tack; trim immediately.",
      "Helm bears away to broad reach on the new gybe.",
    ],
    pitfalls: [
      "Slow pole change — spinnaker collapses in the lee of the main.",
      "Sheet + guy confusion — easy to mix up when both are flying. Mark them clearly.",
      "Death-roll mid-gybe — keep the boat flat with crew weight + ease both sheets if rolling.",
    ],
  },
  {
    id: "trapeze",
    name: "Trapeze",
    course: "NSS L3",
    summary:
      "Suspend crew weight from a wire attached to the mast — moves crew weight far outboard, increasing righting moment.",
    whenToUse:
      "Heavy air + close-hauled sailing on twin-sail dinghies (49er, 470, Laser 2). Lets crew weight balance the rig much further out than hiking allows.",
    steps: [
      "Crew clips trapeze hook (on the harness) onto the wire ring.",
      "Crew slides feet to the side deck near the shroud chainplate.",
      "Crew leans BACK against the wire while keeping legs straight.",
      "Body weight transferred from boat to wire — boat heels less.",
      "To come in: bend knees + grab the gunwale + step back in.",
      "ALWAYS clip in BEFORE stepping out + un-clip AFTER stepping back in.",
    ],
    pitfalls: [
      "Stepping out without clipping in — fall into the water + crewmate is stranded.",
      "Hooking the wire badly — slips off mid-trapezing; falls back into boat or sea.",
      "Forgetting to UN-clip when coming in — get pulled along with the trapeze as boat continues.",
      "Trapezing through a gust without depowering — boat capsizes with crew on the wire (dangerous).",
    ],
  },
]);

// ───────────────────────────────────────────────────────────────────
// 2. Launching + recovery
// ───────────────────────────────────────────────────────────────────

export type LaunchVenue = "slipway" | "beach" | "pontoon";

export interface LaunchProcedure {
  venue: LaunchVenue;
  name: string;
  /// One-line gist.
  summary: string;
  /// Wind / tide considerations.
  conditions: string;
  /// Step-by-step procedure for launching.
  launchSteps: readonly string[];
  /// Step-by-step procedure for recovery.
  recoverySteps: readonly string[];
  /// Pitfalls.
  pitfalls: readonly string[];
}

export const LAUNCH_PROCEDURES: readonly LaunchProcedure[] = Object.freeze([
  {
    venue: "slipway",
    name: "Slipway launch + recovery",
    summary:
      "Trolley or trailer down to a concrete or wooden slipway with car/boat traffic + cross-tide consideration.",
    conditions:
      "Best at HW or close to it (less slipway exposed). Wind direction matters: cross-wind blows boat sideways during transition.",
    launchSteps: [
      "Rig boat off the slipway (mast up, sail covers off, daggerboard ready).",
      "Wheel trolley down the slipway with boat on top.",
      "When boat is half in the water, push it OFF the trolley + into deep enough water for the keel + rudder.",
      "Walk trolley back up to the boat park.",
      "Return to boat in the water; lower rudder + daggerboard.",
      "Step in + sail away from the slipway (typically straight out to seaward into wind).",
    ],
    recoverySteps: [
      "Approach slipway slowly; aim for the trolley if one is positioned, or for the slipway centre.",
      "Sail or row up to where the trolley is.",
      "Lift rudder + daggerboard as boat reaches shallow water.",
      "Once boat is over the trolley, lift bow + pivot boat onto trolley.",
      "Wheel up the slipway + into the boat park.",
    ],
    pitfalls: [
      "Slipway slippery with algae — wear non-slip shoes; risk of falling.",
      "Cross-wind blowing boat off the slipway during launch — get help to steady.",
      "Tide too low — slipway extends underwater; trolley wheels run out of slip; can't recover.",
    ],
  },
  {
    venue: "beach",
    name: "Beach launch + recovery",
    summary:
      "Direct launch off a sand or shingle beach. Common for racing dinghies + holiday hire.",
    conditions:
      "Wave height critical — beach launches are vulnerable to surf rolling boat back. Best in calm conditions or with a steeper beach.",
    launchSteps: [
      "Position trolley + boat at top of beach.",
      "Run trolley + boat down to the water's edge.",
      "Wheel trolley into the water until boat floats off.",
      "Bring trolley back up + leave on the beach above high-water mark.",
      "Return to the boat at the water's edge; push out into deeper water.",
      "Step in over the gunwale + sail away (typically perpendicular to the wave fronts).",
    ],
    recoverySteps: [
      "Approach beach slowly + parallel to the wave fronts.",
      "Lift rudder + daggerboard early — beach is shallow.",
      "Last metres: row or paddle straight onto the beach perpendicular to the waves.",
      "Step out into the water; pull boat clear of the surf zone.",
      "Place trolley under boat in shallow water; lift onto trolley.",
      "Wheel up to the boat park above HW mark.",
    ],
    pitfalls: [
      "Approach broadside to waves — boat swamped or rolled.",
      "Trolley left in the surf — washed away by the next wave.",
      "Recovery in steep wave — boat slammed onto beach + damaged hull.",
      "Forgetting to lift centreboard early — snaps off in the sand.",
    ],
  },
  {
    venue: "pontoon",
    name: "Pontoon launch + recovery (yacht-club tender approach)",
    summary:
      "Boat lifted in by crane or with a hoist; tender approach for recovering boat onto pontoon.",
    conditions:
      "Wind across pontoon is the main concern. Helm needs to control the boat against the wind while crew secures lines.",
    launchSteps: [
      "Yacht club crane / hoist places boat in the water beside pontoon.",
      "Helm + crew board; lines released from hoist.",
      "Helm casts off + sails away perpendicular to the pontoon.",
    ],
    recoverySteps: [
      "Approach pontoon parallel + into wind.",
      "Stop boat against the pontoon; crew steps off with bow line.",
      "Tie up alongside; release crew + helm.",
      "Hoist boat back to the club using the crane.",
    ],
    pitfalls: [
      "Pontoon launch in cross-wind — boat slammed against pontoon; rig damage.",
      "Crew stepping off in too much movement — fall in.",
      "Hoist hooks not aligned — boat tilts; damage.",
    ],
  },
]);
